import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import Login from "./pages/Login";
import Reset from "./pages/Reset";
import Verify from "./pages/VerifyEmail";
import VerifyupdateInfo from "./pages/onBoarding/merchent/VerifyupdateInfo";
import i18next from "i18next";
import en_US from 'antd/locale/en_US';
import ar_EG from 'antd/locale/ar_EG';
import { ConfigProvider } from "antd";
import Layout from "./pages/merchent/Layout";
import OnBoardingLayout from "./pages/onBoarding/Layout";
import { Layout as AdminLayout } from "./pages/admin/layout";
import { Layout as EcrLayout } from "./pages/ecr/layout";
const LazyDash = lazy(() => import("./pages/merchent/Dash"));
const LazyTransactions = lazy(() => import("./pages/merchent/Transactions"));
const LazyManagment = lazy(() => import("./pages/merchent/Managment"));
const LazyRefunds = lazy(() => import("./pages/merchent/Refunds"));
const LazyBulk = lazy(() => import("./pages/merchent/bulkRegister"));
const LazySettings = lazy(() => import("./pages/merchent/settings/index"));
const LazyDiscounts = lazy(() => import("./pages/merchent/discounts/index"));

const LazyRecon = lazy(() => import("./pages/merchent/Reconcilation"));
const LazySettlement = lazy(() => import("./pages/merchent/settlement"));
const LazySettlementReport = lazy(() => import("./pages/merchent/SettlementReportPage"));
const LazyTerminal = lazy(() => import("./pages/merchent/Terminal"));

const LazyTerminals = lazy(() => import("./pages/merchent/Terminals"));
const LazyMerchantOnboarding = lazy(
  () => import("./pages/onBoarding/merchent")
);
const LazyEcrOnboarding = lazy(
  () => import("./pages/onBoarding/ecr")
);
// const LazyMSP = lazy(() => import("./pages/admin/msp"));
const LazyBrands = lazy(() => import("./pages/admin/Brands"));
const LazyMerchants = lazy(() => import("./pages/admin/merchants"));
const LazyEcrProviders = lazy(() => import("./pages/admin/ecrProviders"));
const LazyProviderMappings = lazy(() => import("./pages/admin/providerMappings"));
const LazyProviderMappingDetails = lazy(() => import("./pages/admin/providerMappingDetails"));
const LazyDistricts = lazy(() => import("./pages/admin/districts"));
const LazyAdminMspOnboarding = lazy(() => import("./pages/admin/MspOnboarding"));
const LazyAdminMspOrganizations = lazy(() => import("./pages/admin/mspOrganizations"));
const LazyAdminBilling = lazy(() => import("./pages/admin/Billing"));
const LazyAdminAuditTrail = lazy(() => import("./pages/admin/AuditTrail"));
const LazyAdminAcquirerBrandings = lazy(() => import("./pages/admin/AcquirerBrandings"));

const LazyAdminSettings = lazy(() => import("./pages/admin/settings"));
const LazySyncConfiguration = lazy(() => import("./pages/admin/SyncConfiguration"));
const LazyAdminDailyReport = lazy(() => import("./pages/admin/DailyReport"));
const LazyTerminalCommConfig = lazy(() => import("./pages/admin/TerminalCommConfig"));
const LazyPermissionOwnerType = lazy(() => import("./pages/admin/PermissionOwnerType"));
const LazyDashboard2 = lazy(() => import("./pages/merchent/Dash"));

// MSP portal pages
const LazyMspHome = lazy(() => import("./pages/msp/Home"));
const LazyMspMerchants = lazy(() => import("./pages/msp/Merchants"));
const LazyMspTerminals = lazy(() => import("./pages/msp/Terminals"));
const LazyMspTransactionReport = lazy(() => import("./pages/msp/TransactionReport"));
const LazyMspBilling = lazy(() => import("./pages/msp/Billing"));
const LazyMspAuditTrail = lazy(() => import("./pages/msp/AuditTrail"));
const LazyMspSettings = lazy(() => import("./pages/msp/Settings"));
const LazyMspPendingApprovals = lazy(() => import("./pages/msp/PendingTerminalApprovals"));

// ECR Provider pages
const LazyEcrHome = lazy(() => import("./pages/ecr/Home"));
const LazyEcrMSP = lazy(() => import("./pages/ecr/msp"));
const LazyAdminTerminalOwnership = lazy(() => import("./pages/admin/TerminalOwnership"));
const LazyEcrBrands = lazy(() => import("./pages/ecr/Brands"));
const LazyEcrMerchants = lazy(() => import("./pages/ecr/merchants"));
const LazyEcrDistricts = lazy(() => import("./pages/ecr/districts"));
const LazyEcrSettings = lazy(() => import("./pages/ecr/settings"));

import { Layout as MspLayout } from "./pages/msp/layout";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./pages/NotFound";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReportDownloadPage from "./pages/merchent/Download";
import ReceiptPage from "./components/ReceiptPage";
import { SSEProvider } from "./SSEContext";
import { analytics, trackPageView } from "./utils/analytics";

// Demo Analytics Hook
const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view with analytics utility
    trackPageView(
      location.pathname + location.search,
      document.title
    );
  }, [location]);
};

// Analytics Wrapper Component
const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  useAnalytics();
  return <>{children}</>;
};

const App = () => {
  useEffect(() => {
    const sessionKey = "sessionAlive";
    sessionStorage.setItem(sessionKey, "true"); // Mark session as active

    const handleUnload = () => {
      // If sessionStorage is empty, it means the tab is closing
      setTimeout(() => {
        if (!sessionStorage.getItem(sessionKey)) {
          localStorage.clear(); // Clear only if tab is fully closed
        }
      }, 500);
    };

    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  // Initialize demo analytics on app mount
  useEffect(() => {
    // Analytics is automatically initialized via the singleton
    // Track app initialization
    analytics.trackEvent({
      category: "app",
      action: "initialized",
      label: "NeoPaaS Dashboard",
    });
    
    // Clean up on unmount
    return () => {
      analytics.endSession();
    };
  }, []);

  const primaryColor = "#441752";
  const antdLocale = i18next.language === 'ar' ? ar_EG : en_US;

  return (
    <AuthProvider>
      <SSEProvider>
        <Router>
          <AnalyticsWrapper>
            <ConfigProvider
              locale={antdLocale}
              theme={{
            components: {
              Tabs: {
                inkBarColor: primaryColor,
                itemActiveColor: primaryColor,
                itemSelectedColor: primaryColor,
                itemHoverColor: primaryColor,
              },

              DatePicker: {
                activeBorderColor: primaryColor,
                cellRangeBorderColor: primaryColor,
                hoverBorderColor: primaryColor,
                colorPrimary: primaryColor,
                cellHoverWithRangeBg: primaryColor,
              },

              Table: {
                colorPrimary: primaryColor,
                colorLink: primaryColor,
                colorLinkActive: primaryColor,
              },
              Button: {
                defaultHoverColor: primaryColor,
                defaultHoverBorderColor: primaryColor,
              },

              Input: {
                hoverBorderColor: primaryColor,
              },
              Checkbox: {
                colorPrimary: primaryColor,
                size: 30,
                colorPrimaryHover: primaryColor,
              },

              Radio: {
                buttonSolidCheckedActiveBg: "red",
                buttonSolidCheckedBg: "#ff0000",
                buttonSolidCheckedHoverBg: "#ff0000",
                dotSize: 6,
                buttonCheckedBg: "red",
                buttonBg: "red",
                colorPrimary: primaryColor,
                colorPrimaryHover: "#9154a4",
              },
            },
          }}
        >
            <Routes>
              {/* <Route path="/cuppertino/remalcomplex/login" element={isAutghenticated ? <Navigate to="/" /> : <Login />} /> */}
              <Route path="/neopaas/portal/login" element={<Login />} />
              <Route path="/neopaas/portal/reset" element={<Reset />} />
              <Route path="/neopaas/portal/verify" element={<Verify />} />
              <Route
                path="/neopaas/portal/veryfiUpdateInfo"
                element={<VerifyupdateInfo />}
              />

<Route
              path="/download"
              element={
           
                  
                      <Suspense>
                        <ReportDownloadPage />
                      </Suspense>
                  
                  }
                />

              <Route
                path="/"
                Component={() => {
                  window.location.href = "/home.html";
                  return null;
                }}
              />

              <Route
                path="/get-started"
                Component={() => {
                  window.location.href = "/get-started.html";
                  return null;
                }}
              />

              <Route
                path="/neopaas/portal/onboarding"
                element={
                  <Suspense>
                    <OnBoardingLayout>
                      <LazyMerchantOnboarding />
                    </OnBoardingLayout>
                  </Suspense>
                }
              />

              <Route
                path="/neopaas/portal/ecr-onboarding"
                element={
                  <Suspense>
                    <OnBoardingLayout>
                      <LazyEcrOnboarding />
                    </OnBoardingLayout>
                  </Suspense>
                }
              />

           

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute access="/dashboard"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyDash />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />
              <Route
                path="/terminals"
                element={
                  <ProtectedRoute access="/terminals"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyTerminals />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />
              <Route
                path="/terminal"
                element={
                  <ProtectedRoute access="/terminal"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyTerminal />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute access="/transactions"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyTransactions />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />
              <Route
                path="/management"
                element={
                  <ProtectedRoute access="/management"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyManagment />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />

              <Route
                path="/reconciliation"
                element={
                  <ProtectedRoute access="/reconciliation"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyRecon />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />

              <Route
                path="/refunds"
                element={
                  <ProtectedRoute access="/refunds"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyRefunds />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />

              <Route
                path="/settlement"
                element={
                  <ProtectedRoute access="/settlement"
                    element={
                      <Layout>
                        <Suspense>
                          <LazySettlement />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />
              <Route
                path="/settlement/runs/:id"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        <Suspense>
                          <LazySettlementReport />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />
              <Route
                path="/bulk"
                element={
                  <ProtectedRoute
                    access="/bulk"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyBulk />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute access="/settings"
                    element={
                      <Layout>
                        <Suspense>
                          <LazySettings />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />

              <Route
                path="/discounts"
                element={
                  <ProtectedRoute access="/discounts"
                    element={
                      <Layout>
                        <Suspense>
                          <LazyDiscounts />
                        </Suspense>
                      </Layout>
                    }
                  />
                }
              />

              <Route
                path="/neopass-admin2"
                element={
                  <ProtectedRoute
                    element={
                      <Suspense>
                        <LazyDashboard2 />
                      </Suspense>

                      // <Layout>
                      //   <Transactions />
                      // </Layout>
                    }
                  />
                }
              />

              <Route path="neopass-admin">
                <Route
                  index
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                          <LazyAdminDailyReport />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                {/* <Route
                  path="msps"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyMSP />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                /> */}
                <Route
                  path="brands"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyBrands />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="merchants"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyMerchants />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="districts"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyDistricts />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="ecr-providers"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyEcrProviders />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="provider-mappings"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyProviderMappings />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="msp-onboarding"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminMspOnboarding />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="msp-organizations"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminMspOrganizations />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="terminal-ownership"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminTerminalOwnership />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="billing"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminBilling />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="acquirer-brandings"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminAcquirerBrandings />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="audit-trail"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminAuditTrail />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="provider-mappings/:id"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyProviderMappingDetails />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="settings"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminSettings />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="sync-configuration"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazySyncConfiguration />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="terminal-comm-config"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyTerminalCommConfig />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="permission-owner-types"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyPermissionOwnerType />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

                <Route
                  path="daily-report"
                  element={
                    <ProtectedRoute
                      element={
                        <AdminLayout>
                          <Suspense>
                            <LazyAdminDailyReport />
                          </Suspense>
                        </AdminLayout>
                      }
                    ></ProtectedRoute>
                  }
                />

              </Route>

              <Route path="ecr-provider">
                <Route
                  index
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrHome />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="home"
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrHome />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="msps"
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrMSP />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="brands"
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrBrands />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="merchants"
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrMerchants />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="districts"
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrDistricts />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute
                      element={
                        <EcrLayout>
                          <Suspense>
                            <LazyEcrSettings />
                          </Suspense>
                        </EcrLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
              </Route>

              <Route path="msp-portal">
                <Route
                  index
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspHome />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="home"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspHome />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="merchants"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspMerchants />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="terminals"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspTerminals />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="pending-approvals"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspPendingApprovals />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
              
                <Route
                  path="transaction-report"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspTransactionReport />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="billing"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspBilling />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="audit-trail"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspAuditTrail />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute roles={["MSP_USER"]}
                      element={
                        <MspLayout>
                          <Suspense>
                            <LazyMspSettings />
                          </Suspense>
                        </MspLayout>
                      }
                    ></ProtectedRoute>
                  }
                />
              </Route>

              <Route path="receipts" element={<ReceiptPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
        </ConfigProvider>
          </AnalyticsWrapper>
        </Router>
      </SSEProvider>
      <ToastContainer />
    </AuthProvider>
  );
};

export default App;
